/**
 * S13 — Tab "Fatos" do painel People Memory.
 * Lista paginada de syra_person_memories com filtros (status, attribute, sensitive).
 * Click em linha → drawer com detalhes e ações.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { PeopleMemoryDrawer } from "@/components/admin/PeopleMemoryDrawer";
import { ShieldAlert } from "lucide-react";

const STATUS_OPTIONS = [
  "all",
  "candidate",
  "awaiting_confirmation",
  "confirmed_by_subject",
  "confirmed_by_admin",
  "self_claim",
  "social_perception",
  "rejected_by_subject",
  "disputed",
  "archived",
];

export function PeopleMemoryFacts({ groupId }: { groupId: string }) {
  const [status, setStatus] = useState("all");
  const [attribute, setAttribute] = useState("");
  const [sensitiveOnly, setSensitiveOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const PAGE = 25;

  const { data: facts = [], isLoading } = useQuery({
    queryKey: ["pm-facts", groupId, status, attribute, sensitiveOnly, page],
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      let q = supabase
        .from("syra_person_memories")
        .select("id, subject_jid, subject_display_name, attribute, value, confirmation_status, source_kind, is_sensitive, raw_confidence, effective_confidence, last_used_at, corroboration_count")
        .eq("group_id", groupId)
        .order("last_used_at", { ascending: false, nullsFirst: false })
        .range(page * PAGE, page * PAGE + PAGE - 1);
      if (status !== "all") q = q.eq("confirmation_status", status);
      if (attribute) q = q.ilike("attribute", `%${attribute}%`);
      if (sensitiveOnly) q = q.eq("is_sensitive", true);
      const { data } = await q;
      return data ?? [];
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Atributo (contém)</label>
          <Input value={attribute} onChange={(e) => setAttribute(e.target.value)} className="w-[200px]" />
        </div>
        <Button
          variant={sensitiveOnly ? "default" : "outline"}
          onClick={() => setSensitiveOnly((v) => !v)}
        >
          <ShieldAlert className="w-4 h-4 mr-2" />Apenas sensíveis
        </Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Anterior</Button>
          <Button variant="outline" disabled={facts.length < PAGE} onClick={() => setPage((p) => p + 1)}>Próximo →</Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Pessoa</TableHead>
            <TableHead>Atributo</TableHead>
            <TableHead>Valor</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Conf</TableHead>
            <TableHead>Corrob</TableHead>
            <TableHead>Sensível</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && <TableRow><TableCell colSpan={8} className="text-muted-foreground">Carregando…</TableCell></TableRow>}
          {facts.map((f: any) => (
            <TableRow key={f.id} className="cursor-pointer" onClick={() => setOpenId(f.id)}>
              <TableCell className="font-medium">{f.subject_display_name || f.subject_jid}</TableCell>
              <TableCell><code className="text-xs">{f.attribute}</code></TableCell>
              <TableCell className="max-w-xs truncate">{f.value}</TableCell>
              <TableCell><StatusBadge status={f.confirmation_status} /></TableCell>
              <TableCell><Badge variant="outline">{f.source_kind}</Badge></TableCell>
              <TableCell>{(f.effective_confidence ?? f.raw_confidence ?? 0).toFixed(2)}</TableCell>
              <TableCell>{f.corroboration_count}</TableCell>
              <TableCell>{f.is_sensitive ? <Badge variant="destructive">sim</Badge> : "—"}</TableCell>
            </TableRow>
          ))}
          {!isLoading && facts.length === 0 && (
            <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Nenhum fato.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <PeopleMemoryDrawer factId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: any = ["confirmed_by_subject", "confirmed_by_admin", "self_claim"].includes(status)
    ? "default"
    : status === "disputed" ? "destructive"
    : status === "social_perception" ? "secondary"
    : "outline";
  return <Badge variant={variant}>{status}</Badge>;
}
